import { type MutableRefObject } from 'react'

import { ArrowRight, Cross } from '@factorialco/f0-react/icons/app'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FACTORIAL_APP, PROFESSIONALS_APP } from 'modules/bookkeepers/lib/amplitudeData'
import {
  type ElectiveIncidenceStatus,
  INCIDENCE_STATUSES,
  INHOUSE_INCIDENCE_STATUSES,
  inhouseStatusCopies,
  professionalsStatusCopies
} from 'modules/payroll_bundle/lib/Incidences/Status'

const BULK_ACTIONS = {
  SEND_TO_BOOKKEEPER: 'send-to-bookkepper',
  TO_DO: 'to-do',
  IN_PROGRESS: 'in-progress',
  DONE: 'done',
  DISCARD: 'discard'
} as const

type BulkActionResult = {
  success: boolean
  data: { incidences?: Array<{ id: string }> }
}

interface BulkActionHandlerParams<T> {
  hasInhousePayroll: boolean
  isBookkeeperView: boolean
  bulkChangeIncidenceStatuses: (
    incidences: T[],
    status: ElectiveIncidenceStatus
  ) => Promise<unknown>
  filterVisibleItems?: (selectedItems: {
    itemsStatus: readonly { checked: boolean; item: T }[]
  }) => {
    itemsStatus: readonly { checked: boolean; item: T }[]
  }
}

type SelectedItems<T> = {
  itemsStatus: readonly { checked: boolean; item: T }[]
  selectedIds?: readonly (string | number)[]
  allSelected?: boolean | 'indeterminate'
}

interface SelectionHandlersParams<T extends { id: string }> {
  visibleItemIdsRef: MutableRefObject<Set<string>>
  selectedItemsMapRef: MutableRefObject<Map<string, T>>
  clearSelectedItemsRef: MutableRefObject<(() => void) | null>
}

export const createOnSelectItemsHandler = <T extends { id: string }>({
  visibleItemIdsRef,
  selectedItemsMapRef,
  clearSelectedItemsRef
}: SelectionHandlersParams<T>) => {
  return (selectedItems: SelectedItems<T>, clearSelectedItems: () => void) => {
    clearSelectedItemsRef.current = clearSelectedItems

    const visibleIds = visibleItemIdsRef.current
    selectedItems.itemsStatus.forEach((itemStatus) => {
      if (!visibleIds.has(itemStatus.item.id)) return
      if (itemStatus.checked) {
        selectedItemsMapRef.current.set(itemStatus.item.id, itemStatus.item)
      } else {
        selectedItemsMapRef.current.delete(itemStatus.item.id)
      }
    })

    const nothingChecked = selectedItems.itemsStatus.every((itemStatus) => !itemStatus.checked)
    const isDeselectAll =
      selectedItems.allSelected === false &&
      (selectedItems.selectedIds?.length ?? 0) === 0 &&
      nothingChecked &&
      selectedItemsMapRef.current.size > 0

    if (isDeselectAll) {
      clearSelectedItems()
      selectedItemsMapRef.current = new Map()
    }
  }
}

const getBulkActionSuccessMessage = (
  action: string,
  changedCount: number,
  hasInhousePayroll: boolean
): string => {
  const pluralOptions = { count: changedCount }
  const statusCopies = hasInhousePayroll ? inhouseStatusCopies() : professionalsStatusCopies(false)

  switch (action) {
    case BULK_ACTIONS.DISCARD:
      return changedCount === 1
        ? i18n.t(
            'compensations.employee_updates_data_collection_page.bulk_actions.success_messages.discard.one',
            pluralOptions
          )
        : i18n.t(
            'compensations.employee_updates_data_collection_page.bulk_actions.success_messages.discard.other',
            pluralOptions
          )
    case BULK_ACTIONS.SEND_TO_BOOKKEEPER:
    case BULK_ACTIONS.TO_DO:
      return changedCount === 1
        ? i18n.t(
            'compensations.employee_updates_data_collection_page.bulk_actions.success_messages.send_to_bookkepper.one',
            pluralOptions
          )
        : i18n.t(
            'compensations.employee_updates_data_collection_page.bulk_actions.success_messages.send_to_bookkepper.other',
            pluralOptions
          )
    case BULK_ACTIONS.IN_PROGRESS:
    case BULK_ACTIONS.DONE:
      return i18n.t('incidences.quick_actions.success', {
        incidencesMoved: changedCount,
        status: statusCopies[action === BULK_ACTIONS.IN_PROGRESS ? 'doing' : 'done']
      })
    default:
      return i18n.t('incidences.quick_actions.success', {
        incidencesMoved: changedCount,
        status: ''
      })
  }
}

const handleNoChangesForSendToBookkeeper = (action: string, changedCount: number): boolean => {
  const isSendToBookkeeperAction =
    action === BULK_ACTIONS.SEND_TO_BOOKKEEPER || action === BULK_ACTIONS.TO_DO

  if (changedCount !== 0 || !isSendToBookkeeperAction) return false

  app.setFlash({
    type: 'correct',
    message: i18n.t(
      'compensations.employee_updates_data_collection_page.bulk_actions.no_changes.send_to_bookkepper'
    )
  })
  return true
}

const handlePartialChangesForSendToBookkeeper = (
  action: string,
  changedCount: number,
  selectedCount: number
): boolean => {
  const isSendToBookkeeperAction =
    action === BULK_ACTIONS.SEND_TO_BOOKKEEPER || action === BULK_ACTIONS.TO_DO

  if (!(changedCount > 0 && changedCount < selectedCount && isSendToBookkeeperAction)) return false

  const message =
    changedCount === 1
      ? i18n.t(
          'compensations.employee_updates_data_collection_page.bulk_actions.partial_changes.send_to_bookkepper.one',
          { count: changedCount }
        )
      : i18n.t(
          'compensations.employee_updates_data_collection_page.bulk_actions.partial_changes.send_to_bookkepper.other',
          { count: changedCount }
        )
  app.setFlash({
    type: 'correct',
    message
  })
  return true
}

const handleSuccessMessage = (
  action: string,
  changedCount: number,
  hasInhousePayroll: boolean
): boolean => {
  app.setFlash({
    type: 'correct',
    message: getBulkActionSuccessMessage(action, changedCount, hasInhousePayroll)
  })
  return true
}

export const createBulkActionHandler = <T extends { id: string; status?: string | null }>({
  hasInhousePayroll,
  isBookkeeperView,
  bulkChangeIncidenceStatuses,
  filterVisibleItems
}: BulkActionHandlerParams<T>) => {
  return async (action: string, selectedItems: SelectedItems<T>) => {
    const filteredItems = filterVisibleItems ? filterVisibleItems(selectedItems) : selectedItems

    const selectedIncidences = filteredItems.itemsStatus
      .filter((item) => item.checked)
      .map((item) => item.item)

    const actionStatusMap: Record<string, ElectiveIncidenceStatus> = {
      [BULK_ACTIONS.SEND_TO_BOOKKEEPER]: hasInhousePayroll
        ? INHOUSE_INCIDENCE_STATUSES.to_do
        : INCIDENCE_STATUSES.to_do,
      [BULK_ACTIONS.TO_DO]: hasInhousePayroll
        ? INHOUSE_INCIDENCE_STATUSES.to_do
        : INCIDENCE_STATUSES.to_do,
      [BULK_ACTIONS.IN_PROGRESS]: hasInhousePayroll
        ? INHOUSE_INCIDENCE_STATUSES.doing
        : INCIDENCE_STATUSES.doing,
      [BULK_ACTIONS.DONE]: hasInhousePayroll
        ? INHOUSE_INCIDENCE_STATUSES.done
        : INCIDENCE_STATUSES.done,
      [BULK_ACTIONS.DISCARD]: hasInhousePayroll
        ? INHOUSE_INCIDENCE_STATUSES.discarded
        : INCIDENCE_STATUSES.discarded
    }

    const targetStatus = actionStatusMap[action]
    if (!targetStatus) return

    let incidencesToProcess = selectedIncidences
    if (action === BULK_ACTIONS.DISCARD && !hasInhousePayroll) {
      const notSentStatus = INCIDENCE_STATUSES.in_preparation
      incidencesToProcess = selectedIncidences.filter(
        (incidence) => incidence.status === notSentStatus
      )

      if (incidencesToProcess.length === 0) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'compensations.employee_updates_data_collection_page.bulk_actions.discard_only_not_sent'
          )
        })
        return
      }
    }

    if (action === BULK_ACTIONS.SEND_TO_BOOKKEEPER) {
      const inPreparationStatus = INCIDENCE_STATUSES.in_preparation

      incidencesToProcess = selectedIncidences.filter(
        (incidence) => incidence.status === inPreparationStatus
      )

      if (incidencesToProcess.length === 0) {
        handleNoChangesForSendToBookkeeper(action, 0)
        return
      }
    }

    try {
      const previousStatuses = new Map(
        incidencesToProcess.map((incidence) => [incidence.id, incidence.status])
      )

      const rawResult = await bulkChangeIncidenceStatuses(incidencesToProcess, targetStatus)

      const isBulkActionResult = (value: unknown): value is BulkActionResult => {
        return typeof value === 'object' && value !== null && 'success' in value && 'data' in value
      }

      if (isBulkActionResult(rawResult) && rawResult.success) {
        const changedCount = rawResult.data.incidences?.length || 0
        const selectedCount = selectedIncidences.length

        if (changedCount > 0) {
          const changedIncidenceIds = new Set(
            rawResult.data.incidences?.map((incidence) => incidence.id) || []
          )

          const countsByPreviousStatus = new Map<string, number>()
          changedIncidenceIds.forEach((incidenceId) => {
            const previousStatus = previousStatuses.get(incidenceId)
            if (!previousStatus) return

            const currentCount = countsByPreviousStatus.get(previousStatus) || 0

            countsByPreviousStatus.set(previousStatus, currentCount + 1)
          })

          countsByPreviousStatus.forEach((amount, previousStatus) => {
            track(trackingEvents.compensations.bulkChangeStatusEmployeeUpdates, {
              previous_status: previousStatus,
              new_status: targetStatus,
              application: isBookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
              amount
            })
          })
        }

        if (handleNoChangesForSendToBookkeeper(action, changedCount)) return
        if (handlePartialChangesForSendToBookkeeper(action, changedCount, selectedCount)) return
        handleSuccessMessage(action, changedCount, hasInhousePayroll)
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'compensations.employee_updates_data_collection_page.bulk_actions.error_message'
        )
      })
    }
  }
}

interface OnBulkActionHandlerParams<T extends { id: string }> extends SelectionHandlersParams<T> {
  handleBulkAction: (action: string, selectedItems: SelectedItems<T>) => Promise<unknown>
}

export const createOnBulkActionHandler = <T extends { id: string }>({
  visibleItemIdsRef,
  selectedItemsMapRef,
  clearSelectedItemsRef,
  handleBulkAction
}: OnBulkActionHandlerParams<T>) => {
  return (action: string, selectedItems: SelectedItems<T>) => {
    const merged = new Map(selectedItemsMapRef.current)
    const visibleIds = visibleItemIdsRef.current

    selectedItems.itemsStatus.forEach((itemStatus) => {
      if (itemStatus.checked && visibleIds.has(itemStatus.item.id)) {
        merged.set(itemStatus.item.id, itemStatus.item)
      }
    })

    const allSelectedItems: SelectedItems<T> = {
      itemsStatus: Array.from(merged.values()).map((item) => ({
        checked: true,
        item
      }))
    }

    return handleBulkAction(action, allSelectedItems).finally(() => {
      clearSelectedItemsRef.current?.()
      selectedItemsMapRef.current = new Map()
    })
  }
}

export const getBulkActionsConfig = (hasInhousePayroll: boolean) => {
  if (hasInhousePayroll) {
    return {
      expanded: 4,
      secondary: [],
      primary: [
        {
          label: i18n.t(
            'compensations.employee_updates_data_collection_page.bulk_actions.change_status'
          ),
          id: 'change-status'
        },
        {
          label: i18n.t('compensations.employee_updates_data_collection_page.bulk_actions.to_do'),
          id: BULK_ACTIONS.TO_DO
        },
        {
          label: i18n.t(
            'compensations.employee_updates_data_collection_page.bulk_actions.in_progress'
          ),
          id: BULK_ACTIONS.IN_PROGRESS
        },
        {
          label: i18n.t('compensations.employee_updates_data_collection_page.bulk_actions.done'),
          id: BULK_ACTIONS.DONE
        },
        {
          label: i18n.t('compensations.employee_updates_data_collection_page.bulk_actions.discard'),
          id: BULK_ACTIONS.DISCARD
        }
      ]
    }
  }

  return {
    secondary: [
      {
        label: i18n.t('compensations.employee_updates_data_collection_page.bulk_actions.discard'),
        icon: Cross,
        id: BULK_ACTIONS.DISCARD
      }
    ],
    primary: [
      {
        label: i18n.t(
          'compensations.employee_updates_data_collection_page.bulk_actions.send_to_bookkepper'
        ),
        icon: ArrowRight,
        id: BULK_ACTIONS.SEND_TO_BOOKKEEPER
      }
    ]
  }
}
